import { isPlatformBrowser } from '@angular/common';
import { Component, PLATFORM_ID, inject, signal } from '@angular/core';
import { MatChipListboxChange, MatChipsModule } from '@angular/material/chips';
import { MatAnchor, MatButtonModule } from "@angular/material/button";
import { Router } from '@angular/router';
import { CartService } from '../commons/cart-service';
import { TrackingJwtService } from '../commons/tracking-jwt-service';
import { GalleryApiService } from '../core/api/gallery-api.service';
import { OrderApiService } from '../core/api/order-api.service';
import { ReviewApiService } from '../core/api/review-api.service';
import { GalleryItemView } from '../models/gallery.model';
import { Order } from '../models/order.model';
import { GuestReviewView } from '../models/review.model';
import { ReviewNudgeComponent } from '../reviews/review-nudge';
import { ReviewerAvatarComponent } from '../ui/reviewer-avatar/reviewer-avatar.component';
import { CustomerAuthService } from '../core/auth/customer-auth.service';
import { SeoService } from '../core/seo/seo.service';
import { isHomepageReviewCandidate } from '../utils/review-prompt';

@Component({
  selector: 'app-home-page',
  imports: [MatChipsModule, MatAnchor, MatButtonModule, ReviewNudgeComponent, ReviewerAvatarComponent],
  templateUrl: './home-page.html',
  styleUrl: './home-page.scss',
})
export class HomePage {
  private router = inject(Router);
  private cartService = inject(CartService);
  private galleryApi = inject(GalleryApiService);
  private reviewApi = inject(ReviewApiService);
  private orderApi = inject(OrderApiService);
  private customerAuth = inject(CustomerAuthService);
  private platformId = inject(PLATFORM_ID);
  private seo = inject(SeoService);
  trackingJwtService = inject(TrackingJwtService);
  isUserActionOptionSelected = signal(false);
  navigateToPath = (signal<string | null>(null));
  testimonials = signal<GuestReviewView[]>([]);
  galleryPreview = signal<GalleryItemView[]>([]);
  lastOrder = signal<Order | null>(null);

  constructor() {
    this.seo.applyPage({
      title: 'Unaavu | South Indian Plant-Based Restaurant in Jodhpur',
      description:
        'Authentic Tamil and South Indian plant-based meals in Jodhpur. Order delivery, takeaway, dine-in, or book a table at Unaavu.',
      canonicalPath: '/',
      jsonLd: this.seo.buildRestaurantJsonLd(),
    });

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.reviewApi.list(true).subscribe((reviews) => this.testimonials.set(reviews.slice(0, 6)));
    this.galleryApi.list().subscribe((items) => this.galleryPreview.set(items.slice(0, 4)));
    const trackingJwt = this.trackingJwtService.readPersistedTrackingJwt();
    if (trackingJwt) {
      this.orderApi.getLiveOrders(trackingJwt).subscribe((orders) => {
        const reviewable = orders.find((order) =>
          isHomepageReviewCandidate(order.orderType, order.orderStatus),
        );
        this.lastOrder.set(reviewable ?? null);
      });
    }
  }

  onSelectionChange($event: MatChipListboxChange) {
    this.handleNavigation($event.value);
    this.isUserActionOptionSelected.set(true);
  }

  selectActionAndNavigate(action: string) {
    this.handleNavigation(action);
  }

  private handleNavigation(action: string) {
    switch (action) {
      case 'Order for Delivery':
        this.cartService.selectedOrderType.set('Delivery');
        this.navigateToPath.set('/order?type=delivery');
        break;
      case 'Order Take Away':
        this.cartService.selectedOrderType.set('Takeaway');
        this.navigateToPath.set('/order?type=take-away');
        break;
      case 'Order At Table':
        this.cartService.selectedOrderType.set('Dine-In');
        this.navigateToPath.set('/order?type=at-table');
        break;
      case 'Book a Table':
        this.navigateToPath.set('/book-a-table');
        break;
      case 'My Orders':
        this.navigateToPath.set('/my-orders');
        break;
    }
    this.onClickNavigate();
  }

  onClickNavigate() {
    if (!!this.navigateToPath()) this.router.navigateByUrl(this.navigateToPath()!);
  }

  hasTrackingJwt(): boolean {
    return !!this.trackingJwtService.readPersistedTrackingJwt();
  }

  goToReviews(): void {
    void this.router.navigateByUrl('/reviews');
  }

  goToGallery(): void {
    void this.router.navigateByUrl('/gallery');
  }

  lastOrderDishNames(): string[] {
    return (this.lastOrder()?.cartItems ?? [])
      .map((item) => item.dish?.name)
      .filter((name): name is string => Boolean(name));
  }

  starLabel(rating: number): string {
    return `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
  }

  loggedInGuestName(): string {
    return this.customerAuth.currentCustomer()?.name?.trim() ?? '';
  }
}
